import { quizErrorMessages } from '@repo/lib/messages/quizErrorMessages';
import { isAxiosError } from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import type { QuizMutationReturnType } from '@/hooks/quiz';

import { useBackTo, useQuizParams } from '@/hooks/quiz';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';
import { useQuizResolverStore } from '@/store/quiz-store';

import type { QuizNavigationProps } from '.';

import { useQuizMutations } from '.';

export function useQuizNavigation(quizNavigationProps: QuizNavigationProps) {
  const { freeNavigation, instantMark, onForceSubmit, sectionTimers } = quizNavigationProps;
  const navigate = useNavigate();
  const { backTo } = useBackTo();
  const clear = useQuizResolverStore((state) => state.clear);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const { id, attemptId, sectionNumber, setSectionNumber } = useQuizParams();

  const { state, actions } = useQuizMutations(id, attemptId);
  const timerSectionAccess = sectionTimers.map((t) => t == null || t > 0);
  const finalDestination = backTo ?? state.resultPageUrl;
  /*
    A terminal submit happens once per attempt: it disarms the navigation
    blocker (the leave-quiz dialog must not interrupt the redirect to
    `finalDestination`) and rejects any further submit for the same attempt.
    The ref guards back-to-back clicks, the state drives the render.
  */
  const [isFinishing, setIsFinishing] = useState<boolean>(false);
  const isFinishingRef = useRef<boolean>(false);

  const startFinishing = (): boolean => {
    if (isFinishingRef.current) return false;
    isFinishingRef.current = true;
    setIsFinishing(true);
    return true;
  };

  const leaveQuiz = (): void => {
    navigate(finalDestination, { replace: true });
    clear();
  };

  const onSubmit = async (): Promise<void> => {
    if (!startFinishing()) return;
    await actions.submitQuiz().finally(leaveQuiz);
  };

  const onSave = async (): ReturnType<QuizMutationReturnType['actions']['saveQuiz']> => {
    return actions.saveQuiz(() => onForceSubmit());
  };

  const onSaveAndSubmit = async (): Promise<void> => {
    if (!startFinishing()) return;
    await actions.onSaveAndSubmit().finally(leaveQuiz);
  };

  const onNavigatePrevious = async (): Promise<void> => {
    /*
       reject navigating to previous if:
         - no freeNavigation
         - out of bounds idx
         - time based access denied
     */
    if (!freeNavigation || sectionNumber === 0) return;

    // if can't navigate to -1, check for first available section if exists navigate there
    if (!timerSectionAccess[sectionNumber - 1]) {
      for (let i = sectionNumber - 1; i >= 0; i--) {
        if (timerSectionAccess[i]) {
          onNavigate(i);
          return;
        }
      }
    } else {
      onNavigate(sectionNumber - 1);
    }
  };

  const onNavigateNext = async (): Promise<void> => {
    /*
      reject navigating to next if:
        - out of bounds idx
        - time based access denied
    */
    if (sectionNumber + 1 >= timerSectionAccess.length) return;

    if (!timerSectionAccess[sectionNumber + 1]) {
      for (let i = sectionNumber + 1; i < timerSectionAccess.length; i++) {
        if (timerSectionAccess[i]) {
          onNavigate(i);
          return;
        }
      }
    } else {
      onNavigate(sectionNumber + 1);
    }
  };

  const onSubSubmit = async (): Promise<void> => {
    try {
      await onSave();
      await actions.subSubmitQuiz({ n: sectionNumber, timers: sectionTimers }).then(() => {
        if (!instantMark) {
          onNavigate(sectionNumber + 1);
        }
      });
    } catch (error) {
      if (isAxiosError(error)) {
        toasterMessageHandler(quizErrorMessages, error);
      }
    }
  };

  const onNavigateAvailable = () => {
    for (let i = sectionNumber + 1; i < timerSectionAccess.length; i++) {
      //found accessible section -> navigate to
      if (timerSectionAccess[i]) {
        onNavigate(i);
        return;
      }
    }
    //not found a timer up ahead, move to closest section back
    for (let i = sectionNumber - 1; i >= 0; i--) {
      if (timerSectionAccess[i]) {
        onNavigate(i);
        return;
      }
    }
  };

  const onNavigate = async (number: number): Promise<void> => {
    setSectionNumber(number, { replace: !freeNavigation });

    if (!freeNavigation && number !== sectionNumber) {
      await actions.subSubmitQuiz({ timers: sectionTimers }).catch(() => {});
      return;
    }

    await actions.quickSaveQuiz().catch((error) => {
      toasterMessageHandler(quizErrorMessages, error);
    });
  };

  const scrollToTop = () => {
    scrollContainer.current?.scrollTo({ top: 0 });
  };

  useEffect(() => {
    scrollToTop();
  }, [sectionNumber]);

  return {
    scrollContainer,
    state: {
      sectionNumber,
      quizId: id,
      attemptId: attemptId,
      isPending: state.isPending,
      isFinishing,
    },
    actions: {
      onNavigatePrevious,
      onNavigateNext,
      onNavigate,
      onSubSubmit,
      onSubmit,
      onSaveAndSubmit,
      onNavigateAvailable,
    },
  };
}
